import {useAui} from '@assistant-ui/react';
import React, {
  createContext,
  type MutableRefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {matchPath, useHistory, useLocation} from 'react-router-dom';

import {
  ARIA_PATH,
  ARIA_THREAD_PATH,
  ariaThreadPath,
} from '../../routes/aria/ariaPaths';
import {useViewer} from '../../state/viewer/hooks';
import {useRumGlobalContext} from '../../util/datadog/datadogRum';
import {
  useActiveThread,
  useActiveThreadId,
  useActiveThreadLoadState,
  useIsOnNewChat,
  useStoreSelector,
} from '../chat/store/context';
import {selectHasRunningThread} from '../chat/store/selectors';
import {useAgentChatAvailability} from './useAgentChatAvailability';
import {useWBAgentWindowStore} from './window/useWBAgentWindowStore';

export type WBAgentDisplayMode = 'drawer' | 'page';

/**
 * How the open ARIA surface is presented. `drawer` is the docked panel; `page`
 * spans the content area while the full-page route is the current URL. The
 * same runtime and surface stay mounted across both; only layout and URL
 * change.
 */
export type WBAgentPresentationController = {
  displayMode: WBAgentDisplayMode;
  /** Whether the surface is showing: opened by the user, or on the page route. */
  isOpen: boolean;
  /** True only for the standalone W&B header, which has no full-page route. */
  pageModeDisabled: boolean;
  /** Show the surface in the current display mode. */
  open(): void;
  /** Navigate to the full-page route for the selected thread. */
  expand(): void;
  /** Leave the full-page route, if on it, with the drawer open. */
  collapse(): void;
  /** Close the surface, leaving the full-page route if on it. */
  close(): void;
};

const WBAgentPresentationControllerContext =
  createContext<WBAgentPresentationController | null>(null);

export const useWBAgentPresentationController =
  (): WBAgentPresentationController => {
    const value = useContext(WBAgentPresentationControllerContext);
    if (value == null) {
      throw new Error(
        'useWBAgentPresentationController must be used inside <WBAgentPresentationControllerProvider>'
      );
    }
    return value;
  };

/**
 * Tracks entering and leaving the page route. Arriving there by any path means
 * the chat is open; the drawer is what remains when the page is left. When
 * this mounted session watches the URL move onto the page route, whichever
 * control or link caused it, the page underneath is one history entry back,
 * and `canPopPageEntryRef` records that so leaving can return to it. A cold
 * load on the page route has nothing behind it and leaves the flag false.
 */
function useTrackPageEntry(
  isPageMode: boolean,
  canPopPageEntryRef: React.MutableRefObject<boolean>,
  isCollapsingRef: React.MutableRefObject<boolean>
): void {
  const setOpen = useWBAgentWindowStore(s => s.setOpen);
  const wasPageRef = useRef(isPageMode);
  useEffect(() => {
    if (isPageMode) {
      setOpen(true);
      if (!wasPageRef.current) {
        canPopPageEntryRef.current = true;
      }
    } else {
      canPopPageEntryRef.current = false;
      // Leaving the page by navigation closes the chat; collapse keeps it.
      if (wasPageRef.current && !isCollapsingRef.current) {
        setOpen(false);
      }
      isCollapsingRef.current = false;
    }
    wasPageRef.current = isPageMode;
  }, [canPopPageEntryRef, isCollapsingRef, isPageMode, setOpen]);
}

/**
 * Rendered by the drawer host for every ARIA host. Must render inside the
 * host's Router and its AgentChatProvider. `displayMode: 'page'` is only
 * meaningful without `pageModeDisabled`; the layout follows `displayMode` either
 * way.
 */
export const WBAgentPresentationControllerProvider = ({
  displayMode,
  pageModeDisabled,
  children,
}: {
  displayMode: WBAgentDisplayMode;
  pageModeDisabled: boolean;
  children: React.ReactNode;
}) => {
  const history = useHistory();
  const setOpen = useWBAgentWindowStore(s => s.setOpen);
  const isOpenInStore = useWBAgentWindowStore(s => s.open);
  const selectedThreadId = useSelectedThreadId();
  const isPageMode = displayMode === 'page';
  // The page route counts as open before the store catches up on load.
  const isOpen = isPageMode || isOpenInStore;
  const {isAvailable} = useAgentChatAvailability();
  const hasRunningThread = useStoreSelector(selectHasRunningThread);
  useRumGlobalContext('aria.is_running', hasRunningThread);
  useRumGlobalContext(
    'aria.sidebar_open',
    isAvailable && isOpen && !isPageMode
  );
  const canPopPageEntryRef = useRef(false);
  const isCollapsingRef = useRef(false);
  useTrackPageEntry(isPageMode, canPopPageEntryRef, isCollapsingRef);

  const open = useCallback(() => setOpen(true), [setOpen]);

  const expand = useCallback(() => {
    if (pageModeDisabled || isPageMode) {
      return;
    }
    setOpen(true);
    history.push(
      selectedThreadId == null ? ARIA_PATH : ariaThreadPath(selectedThreadId)
    );
  }, [history, isPageMode, pageModeDisabled, selectedThreadId, setOpen]);

  const leavePage = useCallback(() => {
    if (!isPageMode) {
      return;
    }
    if (canPopPageEntryRef.current) {
      canPopPageEntryRef.current = false;
      history.goBack();
      return;
    }
    // Cold load on the page route: there is no page behind us.
    history.push('/');
  }, [history, isPageMode]);

  const collapse = useCallback(() => {
    setOpen(true);
    isCollapsingRef.current = isPageMode;
    leavePage();
  }, [isPageMode, leavePage, setOpen]);

  const close = useCallback(() => {
    setOpen(false);
    leavePage();
  }, [leavePage, setOpen]);

  const value = useMemo(
    () => ({
      displayMode,
      isOpen,
      pageModeDisabled,
      open,
      expand,
      collapse,
      close,
    }),
    [displayMode, isOpen, pageModeDisabled, open, expand, collapse, close]
  );

  return (
    <WBAgentPresentationControllerContext.Provider value={value}>
      {children}
    </WBAgentPresentationControllerContext.Provider>
  );
};

const routeThreadIdOf = (pathname: string): string | null =>
  matchPath<{threadId: string}>(pathname, {path: ARIA_THREAD_PATH, exact: true})
    ?.params.threadId ?? null;

/** The committed thread the surface shows, or null for an unsent new chat. */
function useSelectedThreadId(): string | null {
  const activeThread = useActiveThread();
  const activeThreadId = useActiveThreadId();
  const isOnNewChat = useIsOnNewChat();
  return isOnNewChat ? null : (activeThread?.id ?? activeThreadId);
}

/**
 * The thread a route-driven load is waiting to see selected, or null when the
 * chat UI owns selection and may write it back to the route.
 */
type PendingRouteRef = MutableRefObject<{threadId: string | null} | null>;

/** Restores the drawer's persisted thread when entering the page root. */
function useRestorePersistedRoute(routeThreadId: string | null): string | null {
  const history = useHistory();
  const viewerId = useViewer()?.id ?? null;
  const setRestoreTarget = useWBAgentWindowStore(s => s.setRestoreTarget);
  const persistedTargetRef = useRef(
    routeThreadId == null
      ? useWBAgentWindowStore.getState().restoreTarget
      : null
  );
  const persistedTarget = persistedTargetRef.current;
  const persistedThreadId =
    // oxlint-disable-next-line react/refs
    persistedTarget != null && persistedTarget.viewerId === viewerId
      ? persistedTarget.threadId
      : null;

  useEffect(() => {
    if (persistedTarget == null) {
      return;
    }
    persistedTargetRef.current = null;
    // NOTE: Drop a target another viewer left without a request. It can never
    // resolve for this one.
    if (persistedTarget.viewerId !== viewerId) {
      setRestoreTarget(null);
      return;
    }
    history.replace(ariaThreadPath(persistedTarget.threadId));
    // oxlint-disable-next-line react/refs
  }, [history, persistedTarget, setRestoreTarget, viewerId]);

  // oxlint-disable-next-line react/refs
  return routeThreadId ?? persistedThreadId;
}

/** Clears navigation state when the selected thread no longer exists. */
function useClearMissingRoute(pendingRouteRef: PendingRouteRef): void {
  const history = useHistory();
  const {failure} = useActiveThreadLoadState();
  const setRestoreTarget = useWBAgentWindowStore(s => s.setRestoreTarget);

  useEffect(() => {
    if (failure?.kind !== 'not-found') {
      return;
    }
    pendingRouteRef.current = {threadId: null};
    setRestoreTarget(null);
    history.replace(ARIA_PATH);
  }, [failure, history, pendingRouteRef, setRestoreTarget]);
}

/** Applies direct links and browser traversal to the chat runtime. */
function useSelectThreadFromRoute(
  routeThreadId: string | null,
  selectedThreadId: string | null,
  pendingRouteRef: PendingRouteRef
): void {
  const aui = useAui();
  const previousRouteThreadIdRef = useRef(routeThreadId);

  useEffect(() => {
    if (previousRouteThreadIdRef.current !== routeThreadId) {
      previousRouteThreadIdRef.current = routeThreadId;
      pendingRouteRef.current = {threadId: routeThreadId};
    }
    if (pendingRouteRef.current?.threadId !== routeThreadId) {
      return;
    }
    if (selectedThreadId === routeThreadId) {
      pendingRouteRef.current = null;
      return;
    }
    if (routeThreadId == null) {
      aui.threads().switchToNewThread();
    } else {
      void aui.threads().switchToThread(routeThreadId);
    }
  }, [aui, pendingRouteRef, routeThreadId, selectedThreadId]);
}

/**
 * Writes chat UI selections to the route without fighting route-driven loads.
 * Always replaces, so the page stays one history entry and `collapse` can pop
 * it.
 */
function useReplaceRouteFromSelection(
  routeThreadId: string | null,
  selectedThreadId: string | null,
  pendingRouteRef: PendingRouteRef
): void {
  const history = useHistory();

  useEffect(() => {
    if (pendingRouteRef.current != null || routeThreadId === selectedThreadId) {
      return;
    }
    history.replace(
      selectedThreadId == null ? ARIA_PATH : ariaThreadPath(selectedThreadId)
    );
  }, [history, pendingRouteRef, routeThreadId, selectedThreadId]);
}

/**
 * Keeps the page route and the session's thread selection in sync while page
 * mode is active. Must render inside AgentChatRuntimeBoundary.
 */
export const WBAgentPageRouteSync = () => {
  const {pathname} = useLocation();
  const routeThreadId = useRestorePersistedRoute(routeThreadIdOf(pathname));
  const selectedThreadId = useSelectedThreadId();
  const pendingRouteRef = useRef<{threadId: string | null} | null>({
    threadId: routeThreadId,
  });

  useClearMissingRoute(pendingRouteRef);
  useSelectThreadFromRoute(routeThreadId, selectedThreadId, pendingRouteRef);
  useReplaceRouteFromSelection(
    routeThreadId,
    selectedThreadId,
    pendingRouteRef
  );

  return null;
};
